import type { ComputedRef, WritableComputedRef } from 'vue'
import type { AppSelectOption } from '@brighterly/lib-core-types/ui/selects/interfaces'

import { useSidebarPrivileges } from '@entities/viewer'
import { useFiltersDraft } from '@shared/lib/filters'
import { fromDateRangePickerValue, toDateRangePickerValue } from '@shared/lib/routes'

import { useSurveysStore } from './store'
import { useSurveysExport } from '../api/export.hook'
import { useSurveyOptions } from './survey-options'
import { SURVEYS_SEARCH_MAX_LENGTH, isSurveyStatus, surveysFiltersToQuery } from '../lib/surveys-query'

interface FiltersSurveysReturn {
  refSearch: WritableComputedRef<string>
  refDateRange: WritableComputedRef<Date[] | null>
  refType: WritableComputedRef<string | undefined>
  refStatuses: WritableComputedRef<string[]>
  refSource: WritableComputedRef<string | undefined>
  refTutorIds: WritableComputedRef<number[]>
  typeOptions: ComputedRef<AppSelectOption[]>
  statusOptions: ComputedRef<AppSelectOption[]>
  sourceOptions: ComputedRef<AppSelectOption[]>
  canFilterByTutor: ComputedRef<boolean>
  searchMaxLength: number
  isLoading: ComputedRef<boolean>
  isExporting: ComputedRef<boolean>
  search: () => Promise<void>
  clearFilters: () => void
  exportSurveys: () => Promise<void>
}

interface FiltersSurveysOptions {
  /** On the discipline Survey tab the page loader also covers the discipline read; elsewhere it is never loading. */
  isContextLoading?: () => boolean
}

/** As in legacy, the controls only prepare the filters: Search (or Enter in the search field) applies them. */
export const useFiltersSurveys = ({ isContextLoading }: FiltersSurveysOptions = {}): FiltersSurveysReturn => {
  const store = useSurveysStore()
  const { canViewTutors } = useSidebarPrivileges()
  const { typeOptions, statusOptions, sourceOptions } = useSurveyOptions()

  const { refDraft, isLoading, patchDraft, search, clearFilters } = useFiltersDraft({
    applied: () => store.getFilters,
    // Typed text keeps its spaces while it is being typed; the applied search is trimmed.
    onApply: filters => store.setFilters({ ...filters, search: filters.search?.trim() || null }),
    onReset: () => store.resetFilters(),
    toQuery: surveysFiltersToQuery,
    loading: () => store.isLoading || !!isContextLoading?.(),
  })

  const refSearch = computed<string>({
    get: () => refDraft.value.search ?? '',
    set: (value) => {
      patchDraft({ search: value.slice(0, SURVEYS_SEARCH_MAX_LENGTH) || null })
    },
  })

  const refDateRange = computed<Date[] | null>({
    get: () => toDateRangePickerValue(refDraft.value),
    set: (value) => {
      patchDraft(fromDateRangePickerValue(value))
    },
  })

  const refType = computed<string | undefined>({
    get: () => refDraft.value.type ?? undefined,
    set: (value) => {
      patchDraft({ type: value ?? null })
    },
  })

  const refStatuses = computed<string[]>({
    get: () => refDraft.value.statuses,
    set: (value) => {
      patchDraft({ statuses: value.filter(isSurveyStatus) })
    },
  })

  const refSource = computed<string | undefined>({
    get: () => refDraft.value.source ?? undefined,
    set: (value) => {
      patchDraft({ source: value ?? null })
    },
  })

  const refTutorIds = computed<number[]>({
    get: () => refDraft.value.tutorIds,
    set: (value) => {
      patchDraft({ tutorIds: value })
    },
  })

  const isExporting = computed<boolean>(() => store.isExporting)

  const exportSurveys = async (): Promise<void> => {
    await useSurveysExport()
  }

  return {
    refSearch,
    refDateRange,
    refType,
    refStatuses,
    refSource,
    refTutorIds,
    typeOptions,
    statusOptions,
    sourceOptions,
    canFilterByTutor: canViewTutors,
    searchMaxLength: SURVEYS_SEARCH_MAX_LENGTH,
    isLoading,
    isExporting,
    search,
    clearFilters,
    exportSurveys,
  }
}
